/**
 * TUVI - CRM Comercial
 * Gestión comercial con Kanban, Hojas de Vida y Dashboard
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import { matchesAnyField } from '../../utils/searchHelper';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import './CRM.css';

// ─── Constantes ────────────────────────────────────────────────────────────────

const ESTADOS = [
  { key: 'vendido',            label: 'Vendido' },
  { key: 'contratado',         label: 'Contratado' },
  { key: 'en_ejecucion',       label: 'En Ejecución' },
];

const COMERCIALES = ['Natalia', 'Ana Sofia', 'Veronica'];

const PRIORIDADES = [
  { key: 'alta',  label: 'Alta' },
  { key: 'media', label: 'Media' },
  { key: 'baja',  label: 'Baja' },
];

const PRIO_ORDEN = { alta: 0, media: 1, baja: 2 };

const TIPOS_DOC = [
  'propuesta_comercial', 'contrato', 'planos', 'cotizacion',
  'acta_inicio', 'otro',
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = d =>
  !d ? '-' : new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });

const formatMoney = v =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);

const getScoreColor = s => s >= 60 ? '#22c55e' : s >= 30 ? '#f59e0b' : '#ef4444';

// El valor del negocio cambió después de la venta (adicionales, retiros o descuentos
// en el proyecto). La tarjeta conserva el valor de venta y muestra el actual aparte.
const cambioValor = h =>
  h.valor_actual != null && h.valor_cotizacion > 0 && Math.abs(h.valor_actual - h.valor_cotizacion) >= 1;

function ValorActual({ hoja, compacto }) {
  if (!cambioValor(hoja)) return null;
  const diff = hoja.valor_actual - hoja.valor_cotizacion;
  const sube = diff > 0;
  return (
    <div
      className="crm-valor-actual"
      title="El valor del proyecto cambió después de la venta. Abre la tarjeta para ver el motivo."
    >
      {compacto ? 'Actual ' : 'Valor actual '}{formatMoney(hoja.valor_actual)}
      <span className={`crm-valor-dif ${sube ? 'sube' : 'baja'}`}>
        {sube ? '▲' : '▼'} {formatMoney(Math.abs(diff))}
      </span>
    </div>
  );
}

const todayStr = () => new Date().toISOString().split('T')[0];

const firstDayOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// ─── Formulario vacío ──────────────────────────────────────────────────────────

const FORM_EMPTY = {
  nombre_cliente: '',
  proyecto_nombre: '',
  apartamento: '',
  area_m2: '',
  comercial: '',
  cedula: '',
  telefono: '',
  correo: '',
  direccion: '',
  prioridad: 'media',
  valor_cotizacion: '',
  fecha_contacto: todayStr(),
};

// ─── Componente principal ──────────────────────────────────────────────────────

export default function CRMComercial() {
  const { success, error, confirm } = useNotification();

  // Tab principal
  const [activeTab, setActiveTab] = useState('kanban');

  // Datos
  const [hojas, setHojas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  // Modal crear / editar
  const [modalForm, setModalForm] = useState(false);
  const [editando, setEditando] = useState(null); // null = crear
  const [formData, setFormData] = useState(FORM_EMPTY);
  const [saving, setSaving] = useState(false);

  // Modal detalle hoja de vida
  const [detalleId, setDetalleId] = useState(null);

  // Dashboard
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [fechaDesde, setFechaDesde] = useState(firstDayOfMonth());
  const [fechaHasta, setFechaHasta] = useState(todayStr());

  // Filtro fechas para kanban / hojas de vida.
  // Por defecto SIN filtro → muestra TODOS los proyectos en sus estados.
  // El usuario puede aplicar un rango manualmente si lo necesita.
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [filtroFechaActivo, setFiltroFechaActivo] = useState({ desde: '', hasta: '' });

  // Drag and drop
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  // ── Cargar hojas ─────────────────────────────────────────────────────────────

  const cargarHojas = useCallback(async () => {
    setLoading(true);
    try {
      const data = await client.query(
        `query($b: String, $fd: String, $fh: String) {
          crmHojasVida(busqueda: $b, fecha_desde: $fd, fecha_hasta: $fh) {
            id cotizacion_id proyecto_id
            nombre_cliente cedula telefono correo
            proyecto_nombre apartamento area_m2
            comercial estado prioridad valor_cotizacion
            fecha_contacto created_at score
            dias_en_estado total_notas total_documentos
            valor_actual total_cambios_valor
          }
        }`,
        { b: busqueda || null, fd: filtroFechaActivo.desde || null, fh: filtroFechaActivo.hasta || null }
      );
      setHojas(data.crmHojasVida || []);
    } catch (e) {
      error('Error cargando datos: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [busqueda, filtroFechaActivo, error]);

  useEffect(() => {
    cargarHojas();
  }, [cargarHojas]);

  // ── Cargar dashboard ─────────────────────────────────────────────────────────

  const cargarDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const data = await client.query(
        `query($fd: String, $fh: String) {
          crmDashboard(fecha_desde: $fd, fecha_hasta: $fh) {
            total_hojas
            por_estado { estado label cantidad valor }
            valor_pipeline
            tasa_conversion
            promedio_dias_cierre
            vendidos_mes
            perdidos_mes
            tendencia_mensual { mes creadas vendidas perdidas }
            ranking_comerciales { comercial total vendidos perdidos tasa }
            alertas_seguimiento {
              id nombre_cliente proyecto_nombre apartamento
              estado dias_sin_movimiento comercial
            }
            funnel { estado label cantidad porcentaje color }
          }
        }`,
        { fd: fechaDesde, fh: fechaHasta }
      );
      setDashData(data.crmDashboard);
    } catch (e) {
      error('Error cargando dashboard: ' + e.message);
    } finally {
      setDashLoading(false);
    }
  }, [fechaDesde, fechaHasta, error]);

  useEffect(() => {
    if (activeTab === 'dashboard') cargarDashboard();
  }, [activeTab, cargarDashboard]);

  // ── Formulario crear / editar ─────────────────────────────────────────────────

  const abrirCrear = () => {
    setEditando(null);
    setFormData(FORM_EMPTY);
    setModalForm(true);
  };

  const abrirEditar = hoja => {
    setEditando(hoja.id);
    setFormData({
      nombre_cliente:   hoja.nombre_cliente || '',
      proyecto_nombre:  hoja.proyecto_nombre || '',
      apartamento:      hoja.apartamento || '',
      area_m2:          hoja.area_m2 || '',
      comercial:        hoja.comercial || '',
      cedula:           hoja.cedula || '',
      telefono:         hoja.telefono || '',
      correo:           hoja.correo || '',
      direccion:        hoja.direccion || '',
      prioridad:        hoja.prioridad || 'media',
      valor_cotizacion: hoja.valor_cotizacion || '',
      fecha_contacto:   hoja.fecha_contacto ? hoja.fecha_contacto.slice(0, 10) : todayStr(),
    });
    setModalForm(true);
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async e => {
    e.preventDefault();
    if (!formData.nombre_cliente.trim()) {
      error('El nombre del cliente es obligatorio');
      return;
    }
    setSaving(true);
    try {
      const input = {
        ...formData,
        area_m2: formData.area_m2 ? parseFloat(formData.area_m2) : null,
        valor_cotizacion: formData.valor_cotizacion ? parseFloat(formData.valor_cotizacion) : null,
      };

      if (editando) {
        const data = await client.mutate(
          `mutation($id: Int!, $input: HojaVidaInput!) {
            editarHojaVida(id: $id, input: $input) { success message }
          }`,
          { id: editando, input }
        );
        if (data.editarHojaVida.success) {
          success('Hoja de vida actualizada');
          setModalForm(false);
          cargarHojas();
        } else {
          error(data.editarHojaVida.message);
        }
      } else {
        const data = await client.mutate(
          `mutation($input: HojaVidaInput!) {
            crearHojaVida(input: $input) { success message id }
          }`,
          { input }
        );
        if (data.crearHojaVida.success) {
          success('Hoja de vida creada');
          setModalForm(false);
          cargarHojas();
        } else {
          error(data.crearHojaVida.message);
        }
      }
    } catch (e) {
      error('Error guardando: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Eliminar ──────────────────────────────────────────────────────────────────

  const handleEliminar = async hoja => {
    const ok = await confirm({
      title: 'Eliminar hoja de vida',
      message: `¿Eliminar la hoja de vida de ${hoja.nombre_cliente}? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      type: 'danger',
    });
    if (!ok) return;
    try {
      await client.mutate(
        `mutation($id: Int!) { eliminarHojaVida(id: $id) { success } }`,
        { id: hoja.id }
      );
      success('Hoja de vida eliminada');
      cargarHojas();
    } catch (e) {
      error('Error eliminando: ' + e.message);
    }
  };

  // ── Drag and Drop ─────────────────────────────────────────────────────────────

  // DRAG DESHABILITADO: Los estados del CRM se sincronizan automáticamente
  // desde cotizaciones y ficha FFE. No se pueden cambiar manualmente.
  const handleDragStart = (e, id) => {
    e.preventDefault();
  };

  const handleDragOver = (e, estadoKey) => {
    // Sin efecto
  };

  const handleDrop = async (e, nuevoEstado) => {
    e.preventDefault();
    error('Los estados del CRM se sincronizan automáticamente desde cotizaciones y ficha FFE. No se pueden cambiar manualmente.');
    setDragOverCol(null);
    setDraggingId(null);
    return;
    // eslint-disable-next-line no-unreachable
    if (!draggingId) return;
    const hoja = hojas.find(h => h.id === draggingId);
    if (!hoja || hoja.estado === nuevoEstado) {
      setDraggingId(null);
      return;
    }
    // Optimistic update
    setHojas(prev => prev.map(h => h.id === draggingId ? { ...h, estado: nuevoEstado } : h));
    setDraggingId(null);
    try {
      const data = await client.mutate(
        `mutation($id: Int!, $e: String!, $n: String) {
          cambiarEstadoCrm(id: $id, estado: $e, nota: $n) { success message }
        }`,
        { id: hoja.id, e: nuevoEstado, n: null }
      );
      if (data.cambiarEstadoCrm.success) {
        success(`Movido a ${ESTADOS.find(s => s.key === nuevoEstado)?.label}`);
      } else {
        error(data.cambiarEstadoCrm.message);
        cargarHojas();
      }
    } catch (e) {
      error('Error cambiando estado: ' + e.message);
      cargarHojas();
    }
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverCol(null);
  };

  // ── Filtrar y ordenar tarjetas ────────────────────────────────────────────────

  // Solo mostrar hojas en estados visibles (vendido, contratado, en_ejecucion)
  const ESTADOS_VISIBLES = ['vendido', 'contratado', 'en_ejecucion'];
  const hojasFiltradas = hojas
    .filter(h => ESTADOS_VISIBLES.includes(h.estado))
    .filter(h => matchesAnyField(h, ['nombre_cliente', 'cedula', 'telefono', 'correo', 'proyecto_nombre', 'apartamento', 'comercial'], busqueda));

  const hojasPorEstado = estado =>
    hojasFiltradas
      .filter(h => h.estado === estado)
      .sort((a, b) => {
        const po = (PRIO_ORDEN[a.prioridad] ?? 1) - (PRIO_ORDEN[b.prioridad] ?? 1);
        if (po !== 0) return po;
        return (b.score || 0) - (a.score || 0);
      });

  const aplicarFiltroFecha = () => {
    setFiltroFechaActivo({ desde: filtroFechaDesde, hasta: filtroFechaHasta });
  };

  const limpiarFiltroFecha = () => {
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
    setFiltroFechaActivo({ desde: '', hasta: '' });
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="crm-container">
      {/* Header */}
      <div className="crm-header">
        <div className="crm-header-left">
          <h2>CRM Comercial</h2>
          <p>Gestión de oportunidades y clientes PARALELO</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="crm-tabs">
            {[
              { key: 'kanban',    label: 'Gestión Comercial' },
              { key: 'hojas',     label: 'Hojas de Vida' },
              { key: 'dashboard', label: 'Dashboard' },
            ].map(t => (
              <button
                key={t.key}
                className={`crm-tab${activeTab === t.key ? ' active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button className="crm-btn crm-btn-primary" onClick={abrirCrear}>
            + Nueva Hoja
          </button>
        </div>
      </div>

      {/* ── Tab: Kanban ─────────────────────────────────────────────────────── */}
      {activeTab === 'kanban' && (
        <>
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: '0.82rem', color: '#1e40af' }}>
            ℹ️ Los estados se sincronizan automáticamente con cotizaciones y ficha FFE. No se pueden cambiar manualmente.
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Buscar por cliente, proyecto, apartamento o comercial..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              style={{ flex: 1, minWidth: 220, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.88rem' }}
            />
            <input type="date" value={filtroFechaDesde} onChange={e => setFiltroFechaDesde(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem' }} />
            <input type="date" value={filtroFechaHasta} onChange={e => setFiltroFechaHasta(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem' }} />
            <button className="crm-btn crm-btn-primary" onClick={aplicarFiltroFecha} disabled={loading}>Filtrar</button>
            {filtroFechaActivo.desde || filtroFechaActivo.hasta
              ? <button className="crm-btn crm-btn-secondary" onClick={limpiarFiltroFecha}>✕ Limpiar</button>
              : null}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Cargando...</div>
          ) : (
            <div className="crm-kanban">
              {ESTADOS.map(estado => {
                const cards = hojasPorEstado(estado.key);
                return (
                  <div
                    key={estado.key}
                    className={`crm-column crm-col-${estado.key}`}
                    onDragOver={e => handleDragOver(e, estado.key)}
                    onDrop={e => handleDrop(e, estado.key)}
                    onDragLeave={() => setDragOverCol(null)}
                  >
                    <div className="crm-col-header">
                      <span className="crm-col-title">{estado.label}</span>
                      <span className="crm-col-count">{cards.length}</span>
                    </div>
                    <div className={`crm-col-body${dragOverCol === estado.key ? ' drag-over' : ''}`}>
                      {cards.map(hoja => (
                        <KanbanCard
                          key={hoja.id}
                          hoja={hoja}
                          dragging={draggingId === hoja.id}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          onClick={() => setDetalleId(hoja.id)}
                          onEditar={() => abrirEditar(hoja)}
                          onEliminar={() => handleEliminar(hoja)}
                        />
                      ))}
                      {cards.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '20px 8px', color: '#cbd5e1', fontSize: '0.78rem' }}>
                          Sin registros
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Tab: Hojas de Vida ──────────────────────────────────────────────── */}
      {activeTab === 'hojas' && (
        <TabHojas
          hojas={hojas}
          loading={loading}
          onVerDetalle={id => setDetalleId(id)}
          onEditar={abrirEditar}
          onEliminar={handleEliminar}
          onRefresh={cargarHojas}
          filtroFechaDesde={filtroFechaDesde}
          filtroFechaHasta={filtroFechaHasta}
          onFechaDesde={setFiltroFechaDesde}
          onFechaHasta={setFiltroFechaHasta}
          onAplicarFiltro={aplicarFiltroFecha}
          onLimpiarFiltro={limpiarFiltroFecha}
          filtroActivo={!!(filtroFechaActivo.desde || filtroFechaActivo.hasta)}
        />
      )}

      {/* ── Tab: Dashboard ──────────────────────────────────────────────────── */}
      {activeTab === 'dashboard' && (
        <TabDashboard
          dashData={dashData}
          loading={dashLoading}
          fechaDesde={fechaDesde}
          fechaHasta={fechaHasta}
          onFechaDesde={setFechaDesde}
          onFechaHasta={setFechaHasta}
          onFiltrar={cargarDashboard}
          onVerDetalle={id => setDetalleId(id)}
        />
      )}

      {/* ── Modal Crear / Editar ────────────────────────────────────────────── */}
      {modalForm && (
        <ModalFormHoja
          editando={editando}
          formData={formData}
          onChange={handleFormChange}
          onSubmit={handleFormSubmit}
          onClose={() => setModalForm(false)}
          saving={saving}
        />
      )}

      {/* ── Modal Detalle Hoja de Vida ──────────────────────────────────────── */}
      {detalleId && (
        <ModalDetalleHoja
          id={detalleId}
          onClose={() => { setDetalleId(null); cargarHojas(); }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Kanban Card
// ══════════════════════════════════════════════════════════════════════════════

function KanbanCard({ hoja, dragging, onDragStart, onDragEnd, onClick, onEditar, onEliminar }) {
  const scoreColor = getScoreColor(hoja.score || 0);
  const alerta = (hoja.dias_en_estado || 0) > 7;

  const prioBorder = { alta: '#ef4444', media: '#f59e0b', baja: '#22c55e' }[hoja.prioridad] || '#e2e8f0';

  return (
    <div
      className="crm-card"
      style={{ borderLeft: `3px solid ${prioBorder}` }}
      onClick={onClick}
    >
      <div className="crm-card-top">
        <span className="crm-card-name">{hoja.nombre_cliente}</span>
        {hoja.prioridad && (
          <span className={`crm-prio crm-prio-${hoja.prioridad}`}>{hoja.prioridad}</span>
        )}
        {alerta && (
          <span title={`${hoja.dias_en_estado} días sin movimiento`} style={{ fontSize: '0.9rem' }}>
            🔴
          </span>
        )}
      </div>

      {(hoja.proyecto_nombre || hoja.apartamento) && (
        <div className="crm-card-proy">
          {hoja.proyecto_nombre}{hoja.apartamento ? ` · Apt ${hoja.apartamento}` : ''}
        </div>
      )}

      {hoja.valor_cotizacion > 0 && (
        <div className="crm-card-valor">
          {cambioValor(hoja) && <span className="crm-valor-etq">Venta </span>}
          {formatMoney(hoja.valor_cotizacion)}
        </div>
      )}
      <ValorActual hoja={hoja} compacto />


      {hoja.comercial && (
        <div className="crm-card-comercial">{hoja.comercial}</div>
      )}

      <div className="crm-card-bottom">
        <div className="crm-card-score" title={`Score: ${hoja.score || 0}`}>
          <div
            className="crm-card-score-fill"
            style={{ width: `${Math.min(hoja.score || 0, 100)}%`, background: scoreColor }}
          />
        </div>
        <span className={`crm-card-dias${alerta ? ' alerta' : ''}`}>
          {hoja.dias_en_estado || 0}d
        </span>
      </div>

      <div className="crm-card-actions" onClick={e => e.stopPropagation()}>
        <button className="crm-card-btn" title="Editar" onClick={onEditar}>✏️</button>
        <button className="crm-card-btn" title="Eliminar" onClick={onEliminar}>🗑️</button>
        {hoja.total_notas > 0 && (
          <span style={{ fontSize: '0.68rem', color: '#94a3b8', marginLeft: 4 }}>
            📝{hoja.total_notas}
          </span>
        )}
        {hoja.total_documentos > 0 && (
          <span style={{ fontSize: '0.68rem', color: '#94a3b8', marginLeft: 4 }}>
            📎{hoja.total_documentos}
          </span>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Tab Hojas de Vida
// ══════════════════════════════════════════════════════════════════════════════

function TabHojas({ hojas, loading, onVerDetalle, onEditar, onEliminar,
  filtroFechaDesde, filtroFechaHasta, onFechaDesde, onFechaHasta, onAplicarFiltro, onLimpiarFiltro, filtroActivo }) {
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroComercial, setFiltroComercial] = useState('');

  // Solo mostrar hojas en estados visibles (vendido, contratado, en_ejecucion)
  const ESTADOS_VISIBLES = ['vendido', 'contratado', 'en_ejecucion'];
  const filtradas = hojas.filter(h => {
    if (!ESTADOS_VISIBLES.includes(h.estado)) return false;
    const matchQ = matchesAnyField(h, ['nombre_cliente', 'proyecto_nombre', 'apartamento', 'cedula', 'telefono', 'correo', 'comercial'], busqueda);
    const matchE = !filtroEstado || h.estado === filtroEstado;
    const matchC = !filtroComercial || h.comercial === filtroComercial;
    return matchQ && matchE && matchC;
  });

  return (
    <div>
      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Buscar por nombre, cédula, correo..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.88rem' }}
        />
        <select
          value={filtroEstado}
          onChange={e => setFiltroEstado(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.88rem' }}
        >
          <option value="">Todos los estados</option>
          {ESTADOS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select
          value={filtroComercial}
          onChange={e => setFiltroComercial(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.88rem' }}
        >
          <option value="">Todos los comerciales</option>
          {COMERCIALES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="date" value={filtroFechaDesde || ''} onChange={e => onFechaDesde(e.target.value)}
          title="Fecha desde"
          style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem' }} />
        <input type="date" value={filtroFechaHasta || ''} onChange={e => onFechaHasta(e.target.value)}
          title="Fecha hasta"
          style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem' }} />
        <button className="crm-btn crm-btn-primary" onClick={onAplicarFiltro} disabled={loading}>Filtrar</button>
        {filtroActivo && <button className="crm-btn crm-btn-secondary" onClick={onLimpiarFiltro}>✕ Limpiar</button>}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Cargando...</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <table className="crm-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Proyecto / Apto</th>
                <th>Comercial</th>
                <th>Estado</th>
                <th>Prioridad</th>
                <th>Valor</th>
                <th>Score</th>
                <th>Días</th>
                <th>Contacto</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>
                    No hay hojas de vida con los filtros seleccionados
                  </td>
                </tr>
              ) : (
                filtradas.map(hoja => {
                  const estadoLabel = ESTADOS.find(s => s.key === hoja.estado)?.label || hoja.estado;
                  const alerta = (hoja.dias_en_estado || 0) > 7;
                  return (
                    <tr key={hoja.id}>
                      <td>
                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{hoja.nombre_cliente}</div>
                        {hoja.cedula && <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>{hoja.cedula}</div>}
                      </td>
                      <td>
                        <div style={{ color: '#6366f1', fontWeight: 600, fontSize: '0.84rem' }}>{hoja.proyecto_nombre || '-'}</div>
                        {hoja.apartamento && <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>Apt {hoja.apartamento}</div>}
                      </td>
                      <td>{hoja.comercial || '-'}</td>
                      <td>
                        <EstadoBadge estado={hoja.estado} label={estadoLabel} />
                      </td>
                      <td>
                        {hoja.prioridad && (
                          <span className={`crm-prio crm-prio-${hoja.prioridad}`}>{hoja.prioridad}</span>
                        )}
                      </td>
                      <td style={{ color: '#059669', fontWeight: 600, fontSize: '0.84rem' }}>
                        {hoja.valor_cotizacion > 0 ? formatMoney(hoja.valor_cotizacion) : '-'}
                        <ValorActual hoja={hoja} compacto />
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 40, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(hoja.score || 0, 100)}%`, height: '100%', background: getScoreColor(hoja.score || 0), borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: getScoreColor(hoja.score || 0) }}>
                            {hoja.score || 0}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span style={{ color: alerta ? '#ef4444' : '#64748b', fontWeight: alerta ? 700 : 400, fontSize: '0.82rem' }}>
                          {alerta ? '🔴 ' : ''}{hoja.dias_en_estado || 0}d
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: '#64748b' }}>{formatDate(hoja.fecha_contacto)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => onVerDetalle(hoja.id)}>
                            Ver
                          </button>
                          <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => onEditar(hoja)}>
                            ✏️
                          </button>
                          <button
                            className="crm-btn crm-btn-sm"
                            style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                            onClick={() => onEliminar(hoja)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <div style={{ padding: '10px 16px', fontSize: '0.8rem', color: '#94a3b8', borderTop: '1px solid #f1f5f9' }}>
            {filtradas.length} de {hojas.length} registros
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Badge de estado
// ══════════════════════════════════════════════════════════════════════════════

function EstadoBadge({ estado, label }) {
  const colors = {
    envio_cotizacion:  { bg: '#e2e8f0', color: '#475569' },
    conceptualizacion: { bg: '#dbeafe', color: '#1e40af' },
    cotizacion_final:  { bg: '#fef3c7', color: '#92400e' },
    vendido:           { bg: '#dcfce7', color: '#166534' },
    contratado:        { bg: '#e0e7ff', color: '#3730a3' },
    en_ejecucion:      { bg: '#d1fae5', color: '#047857' },
  };
  const c = colors[estado] || { bg: '#f1f5f9', color: '#64748b' };
  return (
    <span style={{
      background: c.bg, color: c.color,
      padding: '2px 8px', borderRadius: 12,
      fontSize: '0.72rem', fontWeight: 700,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Tab Dashboard
// ══════════════════════════════════════════════════════════════════════════════

function TabDashboard({ dashData, loading, fechaDesde, fechaHasta, onFechaDesde, onFechaHasta, onFiltrar, onVerDetalle }) {
  const d = dashData;

  return (
    <div>
      {/* Filtro fechas */}
      <div className="crm-dash-filtro">
        <div className="crm-form-group">
          <label>Desde</label>
          <input type="date" value={fechaDesde} onChange={e => onFechaDesde(e.target.value)} />
        </div>
        <div className="crm-form-group">
          <label>Hasta</label>
          <input type="date" value={fechaHasta} onChange={e => onFechaHasta(e.target.value)} />
        </div>
        <button className="crm-btn crm-btn-primary" onClick={onFiltrar} disabled={loading}>
          {loading ? 'Cargando...' : 'Filtrar'}
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Cargando dashboard...</div>
      )}

      {!loading && !d && (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          Seleccione un rango de fechas y presione Filtrar.
        </div>
      )}

      {!loading && d && (
        <>
          {/* KPIs principales */}
          <div className="crm-kpi-grid">
            <div className="crm-kpi">
              <div className="crm-kpi-value">{d.total_hojas || 0}</div>
              <div className="crm-kpi-label">Total Hojas de Vida</div>
            </div>
            <div className="crm-kpi">
              <div className="crm-kpi-value" style={{ color: '#6366f1' }}>
                {d.tasa_conversion != null ? `${Number(d.tasa_conversion).toFixed(1)}%` : '-'}
              </div>
              <div className="crm-kpi-label">Tasa de Conversión</div>
            </div>
            <div className="crm-kpi">
              <div className="crm-kpi-value" style={{ color: '#059669', fontSize: '1.4rem' }}>
                {formatMoney(d.valor_pipeline || 0)}
              </div>
              <div className="crm-kpi-label">Valor Pipeline</div>
            </div>
            <div className="crm-kpi">
              <div className="crm-kpi-value">{d.promedio_dias_cierre != null ? `${Math.round(d.promedio_dias_cierre)}d` : '-'}</div>
              <div className="crm-kpi-label">Prom. Días Cierre</div>
            </div>
          </div>

          {/* KPIs secundarios */}
          <div className="crm-kpi-grid crm-kpi-grid-2" style={{ marginBottom: 16 }}>
            <div className="crm-kpi" style={{ borderLeft: '4px solid #22c55e' }}>
              <div className="crm-kpi-value" style={{ color: '#16a34a' }}>{d.vendidos_mes || 0}</div>
              <div className="crm-kpi-label">Vendidos este mes</div>
            </div>
            <div className="crm-kpi" style={{ borderLeft: '4px solid #ef4444' }}>
              <div className="crm-kpi-value" style={{ color: '#dc2626' }}>{d.perdidos_mes || 0}</div>
              <div className="crm-kpi-label">Perdidos este mes</div>
            </div>
          </div>

          {/* Funnel */}
          {d.funnel && d.funnel.length > 0 && (
            <div className="crm-section">
              <h3>Embudo de Ventas</h3>
              <div className="crm-funnel">
                {d.funnel.map(row => (
                  <div className="crm-funnel-row" key={row.estado}>
                    <div className="crm-funnel-label">{row.label}</div>
                    <div style={{ flex: 1, height: 32, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                      <div
                        className="crm-funnel-bar"
                        style={{
                          width: `${row.porcentaje || 0}%`,
                          background: `linear-gradient(90deg, ${row.color || '#6366f1'}, ${row.color || '#6366f1'}cc)`,
                          minWidth: row.cantidad > 0 ? 48 : 0,
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 12px',
                          boxSizing: 'border-box',
                          transition: 'width 0.5s',
                        }}
                      >
                        {row.cantidad > 0 && (
                          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>
                            {Number(row.porcentaje || 0).toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="crm-funnel-count">{row.cantidad}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tendencia mensual */}
          {d.tendencia_mensual && d.tendencia_mensual.length > 0 && (
            <div className="crm-section">
              <h3>Tendencia Mensual (últimos 12 meses)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={d.tendencia_mensual} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradCreadas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradVendidas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradPerdidas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ fontSize: '0.82rem', borderRadius: 8, border: '1px solid #e2e8f0' }}
                  />
                  <Area type="monotone" dataKey="creadas"  name="Creadas"  stroke="#6366f1" fill="url(#gradCreadas)"  strokeWidth={2} dot={false} />
                  <Area type="monotone" dataKey="vendidas" name="Vendidas" stroke="#22c55e" fill="url(#gradVendidas)" strokeWidth={2} dot={false} />
                  <Area type="monotone" dataKey="perdidas" name="Perdidas" stroke="#ef4444" fill="url(#gradPerdidas)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Ranking + Alertas */}
          <div className="crm-two-cols">
            {/* Ranking comerciales */}
            <div className="crm-section">
              <h3>Ranking Comerciales</h3>
              {d.ranking_comerciales && d.ranking_comerciales.length > 0 ? (
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th>Comercial</th>
                      <th>Total</th>
                      <th>Vendidos</th>
                      <th>Perdidos</th>
                      <th>Tasa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.ranking_comerciales.map((r, i) => (
                      <tr key={r.comercial}>
                        <td>
                          <span style={{ fontWeight: i === 0 ? 700 : 400 }}>
                            {i === 0 ? '🥇 ' : i === 1 ? '🥈 ' : i === 2 ? '🥉 ' : ''}{r.comercial}
                          </span>
                        </td>
                        <td>{r.total}</td>
                        <td style={{ color: '#16a34a', fontWeight: 600 }}>{r.vendidos}</td>
                        <td style={{ color: '#dc2626' }}>{r.perdidos}</td>
                        <td>
                          <span style={{ fontWeight: 700, color: r.tasa >= 50 ? '#16a34a' : '#d97706' }}>
                            {Number(r.tasa || 0).toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ color: '#94a3b8', fontSize: '0.84rem', textAlign: 'center', padding: 20 }}>
                  Sin datos en el período
                </div>
              )}
            </div>

            {/* Alertas de seguimiento */}
            <div className="crm-section">
              <h3>Alertas de Seguimiento</h3>
              {d.alertas_seguimiento && d.alertas_seguimiento.length > 0 ? (
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {d.alertas_seguimiento.map(a => (
                    <div
                      key={a.id}
                      className="crm-alerta-item"
                      style={{ cursor: 'pointer' }}
                      onClick={() => onVerDetalle(a.id)}
                    >
                      <div className="crm-alerta-info">
                        <div style={{ fontWeight: 600 }}>{a.nombre_cliente}</div>
                        <div style={{ color: '#64748b', fontSize: '0.76rem' }}>
                          {a.proyecto_nombre}{a.apartamento ? ` · Apt ${a.apartamento}` : ''}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: '0.74rem' }}>
                          {ESTADOS.find(s => s.key === a.estado)?.label} · {a.comercial}
                        </div>
                      </div>
                      <div className="crm-alerta-dias">{a.dias_sin_movimiento}d sin movimiento</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#94a3b8', fontSize: '0.84rem', textAlign: 'center', padding: 20 }}>
                  Sin alertas activas
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Modal Formulario Crear / Editar
// ══════════════════════════════════════════════════════════════════════════════

function ModalFormHoja({ editando, formData, onChange, onSubmit, onClose, saving }) {
  return (
    <div className="crm-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="crm-modal crm-modal-lg">
        <h3>{editando ? 'Editar Hoja de Vida' : 'Nueva Hoja de Vida'}</h3>
        <form onSubmit={onSubmit}>
          <div className="crm-form-row">
            <div className="crm-form-group">
              <label>Nombre del Cliente *</label>
              <input
                name="nombre_cliente"
                value={formData.nombre_cliente}
                onChange={onChange}
                placeholder="Nombre completo"
                required
              />
            </div>
            <div className="crm-form-group">
              <label>Cédula</label>
              <input name="cedula" value={formData.cedula} onChange={onChange} placeholder="Número de identificación" />
            </div>
          </div>

          <div className="crm-form-row">
            <div className="crm-form-group">
              <label>Teléfono</label>
              <input name="telefono" value={formData.telefono} onChange={onChange} placeholder="+57 300 000 0000" />
            </div>
            <div className="crm-form-group">
              <label>Correo</label>
              <input name="correo" type="email" value={formData.correo} onChange={onChange} placeholder="correo@ejemplo.com" />
            </div>
          </div>

          <div className="crm-form-group">
            <label>Dirección</label>
            <input name="direccion" value={formData.direccion} onChange={onChange} placeholder="Dirección del cliente" />
          </div>

          <div className="crm-form-row">
            <div className="crm-form-group">
              <label>Proyecto</label>
              <input name="proyecto_nombre" value={formData.proyecto_nombre} onChange={onChange} placeholder="Nombre del proyecto" />
            </div>
            <div className="crm-form-group">
              <label>Apartamento</label>
              <input name="apartamento" value={formData.apartamento} onChange={onChange} placeholder="Ej: 301" />
            </div>
          </div>

          <div className="crm-form-row-3">
            <div className="crm-form-group">
              <label>Área (m²)</label>
              <input name="area_m2" type="number" step="0.01" value={formData.area_m2} onChange={onChange} placeholder="0.00" />
            </div>
            <div className="crm-form-group">
              <label>Valor de venta</label>
              <input name="valor_cotizacion" type="number" step="1000" value={formData.valor_cotizacion} onChange={onChange} placeholder="0" />
            </div>
            <div className="crm-form-group">
              <label>Fecha de Contacto</label>
              <input name="fecha_contacto" type="date" value={formData.fecha_contacto} onChange={onChange} />
            </div>
          </div>

          <div className="crm-form-row">
            <div className="crm-form-group">
              <label>Comercial</label>
              <select name="comercial" value={formData.comercial} onChange={onChange}>
                <option value="">Seleccionar...</option>
                {COMERCIALES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="crm-form-group">
              <label>Prioridad</label>
              <select name="prioridad" value={formData.prioridad} onChange={onChange}>
                {PRIORIDADES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <div className="crm-form-actions">
            <button type="button" className="crm-btn crm-btn-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="crm-btn crm-btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : editando ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Modal Detalle Hoja de Vida
// ══════════════════════════════════════════════════════════════════════════════

function ModalDetalleHoja({ id, onClose }) {
  const { success, error, confirm } = useNotification();

  const [hoja, setHoja] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('datos');

  // Cambio de estado
  const [modalEstado, setModalEstado] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [notaEstado, setNotaEstado] = useState('');
  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  // Notas
  const [nuevaNota, setNuevaNota] = useState('');
  const [agregandoNota, setAgregandoNota] = useState(false);
  const [archivosNota, setArchivosNota] = useState([]); // Archivos pendientes a adjuntar
  const notaFileInputRef = React.useRef(null);

  // Documentos
  const [subiendoDoc, setSubiendoDoc] = useState(false);
  const [tipoDoc, setTipoDoc] = useState('propuesta_comercial');
  const fileInputRef = React.useRef(null);

  // Cargar datos
  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [dataHoja, dataTl] = await Promise.all([
        client.query(
          `query($id: Int!) {
            crmHojaVida(id: $id) {
              id cotizacion_id proyecto_id
              nombre_cliente cedula telefono correo direccion
              proyecto_nombre apartamento area_m2
              comercial estado prioridad valor_cotizacion
              fecha_contacto created_at score dias_en_estado
              historial { id estado_anterior estado_nuevo nota usuario fecha_cambio }
              documentos { id tipo nombre key_archivo created_at }
              notas { id contenido usuario created_at archivos { id nombre key_archivo created_at } }
              cotizaciones_relacionadas { id consecutivo valor_final estado fecha }
              valor_cotizacion_bruto descuento_cotizacion cotizacion_consecutivo
              valor_actual
              cambios_valor { tipo concepto motivo valor fecha }
            }
          }`,
          { id }
        ),
        client.query(
          `query($id: Int!) {
            crmTimeline(hoja_vida_id: $id) { tipo titulo descripcion fecha color }
          }`,
          { id }
        ),
      ]);
      setHoja(dataHoja.crmHojaVida);
      setTimeline(dataTl.crmTimeline || []);
    } catch (e) {
      error('Error cargando detalle: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [id, error]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Cambiar estado ──────────────────────────────────────────────────────────

  const handleCambiarEstado = async () => {
    if (!nuevoEstado) { error('Seleccione un estado'); return; }
    setCambiandoEstado(true);
    try {
      const data = await client.mutate(
        `mutation($id: Int!, $e: String!, $n: String) {
          cambiarEstadoCrm(id: $id, estado: $e, nota: $n) { success message }
        }`,
        { id, e: nuevoEstado, n: notaEstado || null }
      );
      if (data.cambiarEstadoCrm.success) {
        success('Estado actualizado');
        setModalEstado(false);
        setNotaEstado('');
        setNuevoEstado('');
        cargar();
      } else {
        error(data.cambiarEstadoCrm.message);
      }
    } catch (e) {
      error('Error: ' + e.message);
    } finally {
      setCambiandoEstado(false);
    }
  };

  // ── Agregar nota ────────────────────────────────────────────────────────────

  const handleAgregarNota = async () => {
    if (!nuevaNota.trim()) { error('Escriba el contenido de la nota'); return; }
    setAgregandoNota(true);
    try {
      const data = await client.mutate(
        `mutation($hid: Int!, $c: String!) {
          agregarNotaCrm(hoja_vida_id: $hid, contenido: $c) { success nota_id }
        }`,
        { hid: id, c: nuevaNota.trim() }
      );
      if (data.agregarNotaCrm.success) {
        const notaId = data.agregarNotaCrm.nota_id;
        // Subir archivos adjuntos a la nota (si hay)
        if (notaId && archivosNota.length > 0 && hoja) {
          const nombreCliente = (hoja.nombre_cliente || 'SIN_CLIENTE').replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ ]/g, '_');
          const proyectoPart  = `${hoja.proyecto_nombre || 'SIN_PROYECTO'}_${hoja.apartamento || 'SIN_APTO'}`.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ _]/g, '_');
          for (const file of archivosNota) {
            try {
              const keyArchivo = `${nombreCliente}/${proyectoPart}/notas/${notaId}_${Date.now()}_${file.name}`;
              const formData = new FormData();
              formData.append('file', file);
              formData.append('bucket', 'crm-comercial');
              formData.append('key', keyArchivo);
              const token = localStorage.getItem('tuvi_token');
              const res = await fetch(`${UPLOAD_URL}/crm-upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
              });
              const json = await res.json();
              if (json.success) {
                await client.mutate(
                  `mutation($nid: Int!, $n: String!, $k: String!) {
                    registrarArchivoNota(nota_id: $nid, nombre: $n, key_archivo: $k) { success }
                  }`,
                  { nid: notaId, n: file.name, k: keyArchivo }
                );
              }
            } catch (err) {
              console.error('Error subiendo archivo de nota:', err);
            }
          }
        }
        success('Nota agregada');
        setNuevaNota('');
        setArchivosNota([]);
        cargar();
      }
    } catch (e) {
      error('Error agregando nota: ' + e.message);
    } finally {
      setAgregandoNota(false);
    }
  };

  const handleSeleccionArchivosNota = e => {
    const files = Array.from(e.target.files || []);
    setArchivosNota(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const handleQuitarArchivoPendiente = idx => {
    setArchivosNota(prev => prev.filter((_, i) => i !== idx));
  };

  const handleEliminarArchivoNota = async arch => {
    const ok = await confirm({ title: 'Eliminar archivo', message: `¿Eliminar "${arch.nombre}"?`, confirmText: 'Eliminar', type: 'danger' });
    if (!ok) return;
    try {
      await client.mutate(
        `mutation($id: Int!) { eliminarArchivoNota(id: $id) { success } }`,
        { id: arch.id }
      );
      success('Archivo eliminado');
      cargar();
    } catch (e) {
      error('Error: ' + e.message);
    }
  };

  // ── Subir documento ─────────────────────────────────────────────────────────

  const handleSubirDoc = async e => {
    const file = e.target.files?.[0];
    if (!file || !hoja) return;
    e.target.value = '';

    const nombreCliente = hoja.nombre_cliente.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ ]/g, '_');
    const proyectoPart  = `${hoja.proyecto_nombre || 'SIN_PROYECTO'}_${hoja.apartamento || 'SIN_APTO'}`.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ _]/g, '_');
    const keyArchivo    = `${nombreCliente}/${proyectoPart}/${tipoDoc}_${file.name}`;

    setSubiendoDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'crm-comercial');
      formData.append('key', keyArchivo);

      const token = localStorage.getItem('tuvi_token');
      const res = await fetch(`${UPLOAD_URL}/crm-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const json = await res.json();

      if (!json.success) throw new Error(json.message || 'Error subiendo archivo');

      const data = await client.mutate(
        `mutation($hid: Int!, $t: String!, $n: String!, $k: String!) {
          registrarDocumentoCrm(hoja_vida_id: $hid, tipo: $t, nombre: $n, key_archivo: $k) { success }
        }`,
        { hid: id, t: tipoDoc, n: file.name, k: keyArchivo }
      );

      if (data.registrarDocumentoCrm.success) {
        success('Documento subido correctamente');
        cargar();
      }
    } catch (e) {
      error('Error subiendo documento: ' + e.message);
    } finally {
      setSubiendoDoc(false);
    }
  };

  // ── Eliminar documento ──────────────────────────────────────────────────────

  const handleEliminarDoc = async doc => {
    const ok = await confirm({ title: 'Eliminar documento', message: `¿Eliminar "${doc.nombre}"?`, confirmText: 'Eliminar', type: 'danger' });
    if (!ok) return;
    try {
      await client.mutate(
        `mutation($id: Int!) { eliminarDocumentoCrm(id: $id) { success } }`,
        { id: doc.id }
      );
      success('Documento eliminado');
      cargar();
    } catch (e) {
      error('Error eliminando: ' + e.message);
    }
  };

  // ── Determinar si documentos están habilitados ──────────────────────────────

  const docsHabilitados = hoja && ['vendido', 'contratado', 'en_ejecucion'].includes(hoja.estado);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="crm-modal-overlay">
        <div className="crm-modal crm-modal-lg" style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ color: '#94a3b8' }}>Cargando...</div>
        </div>
      </div>
    );
  }

  if (!hoja) {
    return (
      <div className="crm-modal-overlay" onClick={onClose}>
        <div className="crm-modal crm-modal-lg">
          <div style={{ color: '#ef4444' }}>No se encontró la hoja de vida.</div>
          <button className="crm-btn crm-btn-secondary" onClick={onClose} style={{ marginTop: 16 }}>Cerrar</button>
        </div>
      </div>
    );
  }

  const estadoLabel = ESTADOS.find(s => s.key === hoja.estado)?.label || hoja.estado;
  const scoreColor  = getScoreColor(hoja.score || 0);

  return (
    <div className="crm-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="crm-modal crm-modal-lg">
        {/* Header */}
        <div className="crm-hv-header">
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem' }}>{hoja.nombre_cliente}</h3>
            <div style={{ color: '#6366f1', fontWeight: 600, fontSize: '0.88rem', marginTop: 2 }}>
              {hoja.proyecto_nombre}{hoja.apartamento ? ` · Apt ${hoja.apartamento}` : ''}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
              <EstadoBadge estado={hoja.estado} label={estadoLabel} />
              {hoja.prioridad && <span className={`crm-prio crm-prio-${hoja.prioridad}`}>{hoja.prioridad}</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="crm-hv-score">
              <div>
                <div className="crm-hv-score-bar">
                  <div className="crm-hv-score-fill" style={{ width: `${Math.min(hoja.score || 0, 100)}%`, background: scoreColor }} />
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>Score</div>
              </div>
              <div className="crm-hv-score-text" style={{ color: scoreColor }}>{hoja.score || 0}</div>
            </div>
            <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#94a3b8' }}>
              {(hoja.dias_en_estado || 0) > 7 && <span style={{ color: '#ef4444' }}>🔴 </span>}
              {hoja.dias_en_estado || 0} días en estado actual
            </div>
            <button
              className="crm-btn crm-btn-secondary crm-btn-sm"
              onClick={onClose}
              style={{ marginTop: 8 }}
            >
              ✕ Cerrar
            </button>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="crm-hv-tabs">
          {[
            { key: 'datos',       label: 'Datos' },
            { key: 'documentos',  label: `Documentos${hoja.documentos?.length ? ` (${hoja.documentos.length})` : ''}` },
            { key: 'notas',       label: `Notas${hoja.notas?.length ? ` (${hoja.notas.length})` : ''}` },
            { key: 'timeline',    label: 'Timeline' },
          ].map(t => (
            <div
              key={t.key}
              className={`crm-hv-tab${activeTab === t.key ? ' active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </div>
          ))}
        </div>

        {/* ── Sub-tab: Datos ──────────────────────────────────────────────── */}
        {activeTab === 'datos' && (
          <div>
            <div className="crm-datos-grid">
              <DatoItem label="Cédula" value={hoja.cedula} />
              <DatoItem label="Teléfono" value={hoja.telefono} />
              <DatoItem label="Correo" value={hoja.correo} />
              <DatoItem label="Dirección" value={hoja.direccion} />
              <DatoItem label="Área" value={hoja.area_m2 ? `${hoja.area_m2} m²` : null} />
              <DatoItem label="Comercial" value={hoja.comercial} />
              <DatoItem label="Valor de venta" value={hoja.valor_cotizacion > 0 ? formatMoney(hoja.valor_cotizacion) : null} />
              <DatoItem label="Valor actual del proyecto" value={hoja.valor_actual != null ? formatMoney(hoja.valor_actual) : null} />
              <DatoItem label="Fecha Contacto" value={formatDate(hoja.fecha_contacto)} />
              <DatoItem label="Creado" value={formatDate(hoja.created_at)} />
            </div>

            {/* De la cotización al valor actual: el descuento de la cotización y,
                después de la venta, cada cambio con su motivo */}
            {(hoja.cambios_valor?.length > 0 || hoja.descuento_cotizacion > 0) && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontWeight: 700, color: '#475569', fontSize: '0.84rem', marginBottom: 8 }}>
                  De la cotización al valor actual
                </div>
                <table className="crm-table crm-cambios-valor">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Concepto</th>
                      <th>Motivo</th>
                      <th style={{ textAlign: 'right' }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hoja.descuento_cotizacion > 0 && (
                      <>
                        <tr>
                          <td></td>
                          <td colSpan={2} style={{ fontWeight: 600 }}>
                            Valor de la cotización{hoja.cotizacion_consecutivo ? ` COT-${hoja.cotizacion_consecutivo}` : ''}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(hoja.valor_cotizacion_bruto)}</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Descuento de la cotización</td>
                          <td className="crm-cv-motivo">Descuento comercial otorgado en la venta</td>
                          <td className="crm-cv-valor baja">−{formatMoney(hoja.descuento_cotizacion)}</td>
                        </tr>
                      </>
                    )}
                    <tr className="crm-cv-base">
                      <td></td>
                      <td colSpan={2}>Valor de venta</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(hoja.valor_cotizacion)}</td>
                    </tr>
                    {hoja.cambios_valor.map((c, i) => (
                      <tr key={i}>
                        <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{c.fecha ? formatDate(c.fecha) : '-'}</td>
                        <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{c.concepto}</td>
                        <td className="crm-cv-motivo">{c.motivo || '-'}</td>
                        <td className={`crm-cv-valor ${c.valor >= 0 ? 'sube' : 'baja'}`}>
                          {c.valor >= 0 ? '+' : '−'}{formatMoney(Math.abs(c.valor))}
                        </td>
                      </tr>
                    ))}
                    {hoja.valor_actual != null && (
                      <tr className="crm-cv-total">
                        <td></td>
                        <td colSpan={2}>Valor actual del proyecto</td>
                        <td style={{ textAlign: 'right' }}>{formatMoney(hoja.valor_actual)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Cotizaciones relacionadas */}
            {hoja.cotizaciones_relacionadas && hoja.cotizaciones_relacionadas.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontWeight: 700, color: '#475569', fontSize: '0.84rem', marginBottom: 8 }}>
                  Cotizaciones Relacionadas
                </div>
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th>Consecutivo</th>
                      <th>Valor</th>
                      <th>Estado</th>
                      <th>Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hoja.cotizaciones_relacionadas.map(cot => (
                      <tr key={cot.id}>
                        <td style={{ fontWeight: 600 }}>#{cot.consecutivo}</td>
                        <td style={{ color: '#059669' }}>{formatMoney(cot.valor_final)}</td>
                        <td>{cot.estado}</td>
                        <td style={{ color: '#64748b' }}>{formatDate(cot.fecha)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Sub-tab: Documentos ─────────────────────────────────────────── */}
        {activeTab === 'documentos' && (
          <div>
            {!docsHabilitados && (
              <div style={{
                padding: '12px 16px', background: '#fef3c7', border: '1px solid #fde68a',
                borderRadius: 8, marginBottom: 16, fontSize: '0.84rem', color: '#92400e',
              }}>
                La carga de documentos está disponible a partir del estado <strong>Vendido</strong>.
              </div>
            )}

            {docsHabilitados && (
              <div style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
                <select
                  value={tipoDoc}
                  onChange={e => setTipoDoc(e.target.value)}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.84rem' }}
                >
                  {TIPOS_DOC.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                </select>
                <button
                  className="crm-btn crm-btn-primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={subiendoDoc}
                >
                  {subiendoDoc ? 'Subiendo...' : '+ Subir Documento'}
                </button>
                <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleSubirDoc} />
              </div>
            )}

            {hoja.documentos && hoja.documentos.length > 0 ? (
              <div className="crm-docs-grid">
                {hoja.documentos.map(doc => {
                  const baseUrl = `${UPLOAD_URL}/crm-doc/${encodeURIComponent(doc.key_archivo)}`;
                  const urlInline = `${baseUrl}?inline=1`;
                  return (
                  <div key={doc.id} className="crm-doc-card">
                    <div className="crm-doc-info">
                      <div className="crm-doc-icon" style={{ background: '#e0e7ff' }}>📎</div>
                      <div className="crm-doc-text">
                        <div className="crm-doc-titulo">
                          <span className="crm-doc-tipo-chip">{doc.tipo?.replace(/_/g, ' ')}</span>
                          <span className="crm-doc-fecha">{formatDate(doc.created_at)}</span>
                        </div>
                        <div className="crm-doc-nombre">{doc.nombre}</div>
                      </div>
                    </div>
                    <div className="crm-doc-acciones">
                      <button
                        className="crm-doc-btn crm-doc-btn-ver"
                        onClick={() => window.open(urlInline, '_blank', 'noopener,noreferrer')}
                        title="Ver en nueva pestaña"
                        type="button"
                      >
                        👁
                      </button>
                      <a
                        className="crm-doc-btn crm-doc-btn-dl"
                        href={baseUrl}
                        download={doc.nombre}
                        title="Descargar"
                      >
                        ⬇
                      </a>
                      {docsHabilitados && (
                        <button
                          className="crm-doc-btn crm-doc-btn-del"
                          onClick={() => handleEliminarDoc(doc)}
                          title="Eliminar"
                          type="button"
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: '0.84rem' }}>
                No hay documentos adjuntos
              </div>
            )}
          </div>
        )}

        {/* ── Sub-tab: Notas ──────────────────────────────────────────────── */}
        {activeTab === 'notas' && (
          <div>
            <div className="crm-nota-form">
              <textarea
                value={nuevaNota}
                onChange={e => setNuevaNota(e.target.value)}
                placeholder="Agregar nota de seguimiento..."
                rows={2}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                <button
                  type="button"
                  className="crm-btn crm-btn-secondary crm-btn-sm"
                  onClick={() => notaFileInputRef.current?.click()}
                  disabled={agregandoNota}
                  title="Adjuntar archivos o imágenes"
                >
                  📎 Adjuntar
                </button>
                <button
                  className="crm-btn crm-btn-primary"
                  onClick={handleAgregarNota}
                  disabled={agregandoNota || !nuevaNota.trim()}
                >
                  {agregandoNota ? '...' : 'Agregar'}
                </button>
              </div>
              <input
                ref={notaFileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                style={{ display: 'none' }}
                onChange={handleSeleccionArchivosNota}
              />
            </div>

            {/* Lista de archivos pendientes */}
            {archivosNota.length > 0 && (
              <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, padding: 10, marginBottom: 12 }}>
                <div style={{ fontSize: '0.76rem', color: '#64748b', fontWeight: 600, marginBottom: 6 }}>
                  Archivos por adjuntar ({archivosNota.length}):
                </div>
                {archivosNota.map((f, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '0.82rem' }}>
                    <span style={{ color: '#475569' }}>📎 {f.name} <span style={{ color: '#94a3b8' }}>({Math.round(f.size / 1024)} KB)</span></span>
                    <button
                      onClick={() => handleQuitarArchivoPendiente(i)}
                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.9rem' }}
                      title="Quitar"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {hoja.notas && hoja.notas.length > 0 ? (
              [...hoja.notas].reverse().map(nota => (
                <div key={nota.id} className="crm-nota-item">
                  <div className="crm-nota-content">{nota.contenido}</div>
                  {nota.archivos && nota.archivos.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {nota.archivos.map(arch => {
                        const esImagen = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(arch.nombre);
                        return (
                          <div key={arch.id} style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            background: '#eef2ff', border: '1px solid #c7d2fe',
                            borderRadius: 6, padding: '4px 8px', fontSize: '0.78rem',
                          }}>
                            <span>{esImagen ? '🖼️' : '📎'}</span>
                            <a
                              href={`${UPLOAD_URL}/crm-doc/${encodeURIComponent(arch.key_archivo)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#4338ca', textDecoration: 'none', fontWeight: 600 }}
                            >
                              {arch.nombre}
                            </a>
                            <button
                              onClick={() => handleEliminarArchivoNota(arch)}
                              style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 0, fontSize: '0.82rem' }}
                              title="Eliminar archivo"
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="crm-nota-meta">
                    <span>{nota.usuario || 'Sistema'}</span>
                    <span>{formatDate(nota.created_at)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: '0.84rem' }}>
                No hay notas registradas
              </div>
            )}
          </div>
        )}

        {/* ── Sub-tab: Timeline ───────────────────────────────────────────── */}
        {activeTab === 'timeline' && (
          <div>
            {timeline.length > 0 ? (
              <ul className="crm-timeline">
                {timeline.map((item, idx) => (
                  <li key={idx} className="crm-tl-item">
                    <div className="crm-tl-dot" style={{ background: item.color || '#6366f1' }} />
                    <div className="crm-tl-content">
                      <div className="crm-tl-title">{item.titulo}</div>
                      {item.descripcion && <div className="crm-tl-desc">{item.descripcion}</div>}
                      <div className="crm-tl-fecha">{formatDate(item.fecha)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : hoja.historial && hoja.historial.length > 0 ? (
              <ul className="crm-timeline">
                {hoja.historial.map((h, idx) => (
                  <li key={h.id || idx} className="crm-tl-item">
                    <div className="crm-tl-dot" style={{ background: '#6366f1' }} />
                    <div className="crm-tl-content">
                      <div className="crm-tl-title">
                        Estado: {ESTADOS.find(s => s.key === h.estado_anterior)?.label || h.estado_anterior}
                        {' → '}
                        {ESTADOS.find(s => s.key === h.estado_nuevo)?.label || h.estado_nuevo}
                      </div>
                      {h.nota && <div className="crm-tl-desc">{h.nota}</div>}
                      <div className="crm-tl-fecha">{h.usuario} · {formatDate(h.fecha_cambio)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: '0.84rem' }}>
                Sin eventos en el timeline
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modal Cambio de Estado ──────────────────────────────────────────── */}
      {modalEstado && (
        <div className="crm-modal-overlay" style={{ zIndex: 1100 }} onClick={e => e.target === e.currentTarget && setModalEstado(false)}>
          <div className="crm-modal">
            <h3>Cambiar Estado</h3>
            <div className="crm-form-group">
              <label>Nuevo Estado</label>
              <select value={nuevoEstado} onChange={e => setNuevoEstado(e.target.value)}>
                <option value="">Seleccionar...</option>
                {ESTADOS.map(s => (
                  <option key={s.key} value={s.key} disabled={s.key === hoja.estado}>
                    {s.label}{s.key === hoja.estado ? ' (actual)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="crm-form-group">
              <label>Nota (opcional)</label>
              <textarea
                value={notaEstado}
                onChange={e => setNotaEstado(e.target.value)}
                placeholder="Observación sobre el cambio de estado..."
                rows={3}
              />
            </div>
            <div className="crm-form-actions">
              <button className="crm-btn crm-btn-secondary" onClick={() => setModalEstado(false)} disabled={cambiandoEstado}>
                Cancelar
              </button>
              <button className="crm-btn crm-btn-primary" onClick={handleCambiarEstado} disabled={cambiandoEstado || !nuevoEstado}>
                {cambiandoEstado ? 'Guardando...' : 'Cambiar Estado'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helper: DatoItem ──────────────────────────────────────────────────────────

function DatoItem({ label, value }) {
  return (
    <div className="crm-dato">
      <div className="crm-dato-label">{label}</div>
      <div className="crm-dato-value">{value || '-'}</div>
    </div>
  );
}
